import React, { useState } from 'react';
import { useInfiniteQuery } from 'react-query';

import styled from 'styled-components';

import { getActivities } from 'apis/ActivitiesAPI';
import ActivityFeed from 'components/Activities/ActivityFeed';
import { ACTIVITY_EMAIL_RECIPIENT_ROLE } from 'components/Activities/constants';
import { CommunityActivityType, PaginatedCommunityActivitiesType } from 'types/communities';

import ActivitiesSidebar from './ActivitiesSidebar';
import Header from '../common/header/Header';
import { useCommunityInfoContext } from '../CommunityInfoContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  height: calc(100vh - 220px);
`;

const Content = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  width: 70%;
  align-self: center;
  background: var(--background-gray-soft);
  border-radius: 10px;
  border: 1px solid var(--line-gray-bold);
`;

const FeedSection = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  height: 100%;
`;

const Activities = () => {
  const { communityInfo } = useCommunityInfoContext();
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isLoading, refetch } =
    useInfiniteQuery<PaginatedCommunityActivitiesType>(
      ['community', 'activities', communityInfo?.id],
      ({ pageParam = null }) => getActivities(communityInfo?.id as number, { nextPageUrl: pageParam }),
      {
        enabled: Boolean(communityInfo?.id),
        refetchOnWindowFocus: false,
        getNextPageParam: (lastPage) => lastPage.next || undefined,
      }
    );

  const activities = data?.pages.flatMap((page) => page.results) ?? [];

  const filteredActivities = selectedEmail
    ? activities.filter((activity) =>
        activity.email?.recipients.some(
          (recipient) => recipient.role === ACTIVITY_EMAIL_RECIPIENT_ROLE.FROM && recipient.email === selectedEmail
        )
      )
    : activities;

  const handleSelectEmail = (email: string) => {
    setSelectedEmail((currentEmail) => (currentEmail === email ? null : email));
  };

  return (
    <Container>
      <Header title="Communication" />
      <Content>
        <FeedSection>
          <ActivityFeed
            activities={filteredActivities}
            communityId={communityInfo?.id}
            hasNextPage={hasNextPage}
            isFetching={isFetching}
            isFetchingNextPage={isFetchingNextPage}
            isLoading={isLoading}
            emptyText={
              selectedEmail
                ? `No activities found for ${selectedEmail}.`
                : 'No activities have been recorded for this community yet.'
            }
            onLoadMore={() => fetchNextPage()}
            onActivityUpdated={() => refetch()}
          />
        </FeedSection>
        <ActivitiesSidebar activities={activities} selectedEmail={selectedEmail} onSelectEmail={handleSelectEmail} />
      </Content>
    </Container>
  );
};

export default Activities;
